import { useState } from 'react'

interface ListingSort {
  field: 'createdAt' | 'updatedAt' | 'price' | 'title'
  direction: 'asc' | 'desc'
}

interface SortDropdownProps {
  sort?: ListingSort
  onSortChange: (sort: ListingSort | undefined) => void
}

const sortOptions = [
  { value: undefined, label: 'Ending Soon' },
  { value: { field: 'createdAt' as const, direction: 'desc' as const }, label: 'Newest' },
  { value: { field: 'createdAt' as const, direction: 'asc' as const }, label: 'Oldest' },
  { value: { field: 'price' as const, direction: 'asc' as const }, label: 'Price: Low to High' },
  { value: { field: 'price' as const, direction: 'desc' as const }, label: 'Price: High to Low' },
  { value: { field: 'title' as const, direction: 'asc' as const }, label: 'Title: A to Z' },
  { value: { field: 'title' as const, direction: 'desc' as const }, label: 'Title: Z to A' }
]

export default function SortDropdown({ sort, onSortChange }: SortDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  
  const currentOption = sortOptions.find(option => {
    if (!sort && !option.value) return true
    if (!sort || !option.value) return false
    return sort.field === option.value.field && sort.direction === option.value.direction
  }) || sortOptions[0]

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
        style={{
          backgroundColor: 'var(--surface-primary)',
          borderColor: 'var(--border-primary)',
          color: 'var(--text-primary)'
        }}
      >
        <svg 
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <line x1={4} y1={21} x2={4} y2={14} />
          <line x1={4} y1={10} x2={4} y2={3} />
          <line x1={12} y1={21} x2={12} y2={12} />
          <line x1={12} y1={8} x2={12} y2={3} />
          <line x1={20} y1={21} x2={20} y2={16} />
          <line x1={20} y1={12} x2={20} y2={3} />
          <line x1={1} y1={14} x2={7} y2={14} />
          <line x1={9} y1={8} x2={15} y2={8} />
          <line x1={17} y1={16} x2={23} y2={16} />
        </svg>
        {currentOption.label}
        <svg 
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-48">
            {sortOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => {
                  onSortChange(option.value)
                  setIsOpen(false)
                }}
                className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                  option === currentOption ? 'bg-gray-50 text-brand-primary font-medium' : 'text-gray-700'
                } ${
                  index === 0 ? 'rounded-t-lg' : ''
                } ${
                  index === sortOptions.length - 1 ? 'rounded-b-lg' : ''
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}